import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';

import { environment } from '../../../../environments/environment';
import {
  INTERNAL_EXAM_SESSION_MODE_VALUES,
  InternalExamDeviceVm,
} from '../../students/internal-exam/internal-exam.models';
import { InternalExamService } from '../../students/internal-exam/internal-exam.service';

/**
 * "Ustawienia OSK → Stanowiska": pairs a computer as an exam station with the code it shows, so the
 * OSK password is never typed on a computer the students use. The QR code on the station opens
 * this tab with the code filled in; pairing still takes a click and a confirmation that names the
 * code, so a QR code or link from someone else cannot pair a foreign computer by itself.
 * Stations that are no longer used can be removed here; such a computer needs a new pairing.
 */
@Component({
  selector: 'app-stations-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    ConfirmDialogModule,
    InputTextModule,
    ToastModule,
    TooltipModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './stations-settings.component.html',
  styleUrl: './stations-settings.component.scss',
})
export class StationsSettingsComponent implements OnInit, OnDestroy {
  private readonly _fb = inject(FormBuilder);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _messageService = inject(MessageService);
  private readonly _confirmationService = inject(ConfirmationService);
  private readonly _internalExamService = inject(InternalExamService);

  public pairing = false;
  public pairingError: string | null = null;
  public loadingDevices = false;
  public devices: InternalExamDeviceVm[] = [];
  public deletingDeviceGuid: string | null = null;
  public readonly internalExamUrl = environment.internalExamUrl.replace(/\/+$/, '');
  /** The code came from the station's QR code and was not edited: it is shown in bold. */
  public codeFromQr = false;
  /** With a code from the QR code only the click is left, so the pairing button pulses until it is clicked. */
  public promptPairing = false;

  /** Eight characters from the station screen; the dash and the letter case do not matter. */
  public readonly pairingForm = this._fb.nonNullable.group({
    code: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9]{4}-?[A-Za-z0-9]{4}$/)]],
  });

  private _devicesRefreshTimer?: ReturnType<typeof setTimeout>;

  public ngOnInit(): void {
    const code = this._route.snapshot.queryParamMap.get('code');
    if (code) {
      this.pairingForm.controls.code.setValue(this.formatCode(code));
      this.codeFromQr = this.pairingForm.valid;
      this.promptPairing = this.codeFromQr;
    }
    this.loadDevices();
  }

  public ngOnDestroy(): void {
    clearTimeout(this._devicesRefreshTimer);
  }

  /** Upper case and the dash after four characters, the way the station shows the code. */
  public onCodeInput(): void {
    this.codeFromQr = false;
    this.promptPairing = false;
    const control = this.pairingForm.controls.code;
    const formatted = this.formatCode(control.value);
    if (formatted !== control.value) {
      control.setValue(formatted, { emitEvent: false });
    }
  }

  public pairStation(): void {
    this.promptPairing = false;
    if (this.pairingForm.invalid) {
      this.pairingForm.markAllAsTouched();
      return;
    }

    const pairingCode = this.pairingForm.controls.code.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    this.pairingError = null;
    this._confirmationService.confirm({
      key: 'stationPairingConfirm',
      header: 'Parowanie stanowiska',
      message:
        `Sparować stanowisko z kodem <strong>${this.formatCode(pairingCode)}</strong>?<br><br>` +
        'Sprawdź, czy ten sam kod widać teraz na ekranie stanowiska w Twoim ośrodku. ' +
        'Sparowany komputer będzie mógł prowadzić egzaminy i wykłady Twoich kursantów.',
      acceptLabel: 'Sparuj',
      rejectLabel: 'Anuluj',
      defaultFocus: 'reject',
      accept: () => this.approvePairing(pairingCode),
    });
  }

  public loadDevices(): void {
    this.loadingDevices = true;
    this._internalExamService.getDevices().subscribe({
      next: (devices) => {
        this.devices = devices;
        this.loadingDevices = false;
      },
      error: () => {
        this.loadingDevices = false;
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się pobrać listy stanowisk.',
        });
      },
    });
  }

  public requestDeviceDeletion(device: InternalExamDeviceVm): void {
    let consequence =
      'Komputer przestanie być stanowiskiem Twojego OSK. Żeby znów go używać, trzeba będzie sparować go ponownie kodem.';
    if (device.hasActiveSession) {
      const activity =
        device.activeSessionMode === INTERNAL_EXAM_SESSION_MODE_VALUES.lectures ? 'wykład' : 'egzamin';
      consequence += `<br><br><strong>Na tym stanowisku trwa teraz ${activity}</strong> – zostanie przerwany.`;
    } else if (device.online) {
      consequence += '<br><br>Stanowisko jest teraz połączone – od razu wyloguje się z konta OSK.';
    }

    this._confirmationService.confirm({
      key: 'stationDeleteConfirm',
      header: 'Usuwanie stanowiska',
      message: `Usunąć stanowisko <strong>${this.escapeHtml(device.name)}</strong>?<br><br>${consequence}`,
      acceptLabel: 'Usuń',
      rejectLabel: 'Anuluj',
      acceptButtonStyleClass: 'p-button-danger',
      defaultFocus: 'reject',
      accept: () => this.deleteDevice(device),
    });
  }

  private deleteDevice(device: InternalExamDeviceVm): void {
    this.deletingDeviceGuid = device.guid;
    this._internalExamService.deleteDevice(device.guid).subscribe({
      next: () => {
        this.deletingDeviceGuid = null;
        this.devices = this.devices.filter((item) => item.guid !== device.guid);
        this._messageService.add({
          severity: 'success',
          summary: 'Stanowisko usunięte',
          detail: `Stanowisko „${device.name}” zostało usunięte.`,
        });
      },
      error: (error: HttpErrorResponse) => {
        this.deletingDeviceGuid = null;
        if (error.status === 404) {
          // Removed in the meantime, e.g. in another tab: the list only needs a refresh.
          this.loadDevices();
          return;
        }
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail:
            error.status === 403
              ? 'To konto nie może usuwać stanowisk tego OSK.'
              : 'Nie udało się usunąć stanowiska. Spróbuj ponownie.',
        });
      },
    });
  }

  private approvePairing(pairingCode: string): void {
    this.pairing = true;
    this._internalExamService.approvePairing(pairingCode).subscribe({
      next: (result) => {
        this.pairing = false;
        this.pairingForm.reset();
        this.codeFromQr = false;
        // A reload must not offer the used code again.
        void this._router.navigate([], {
          relativeTo: this._route,
          queryParams: { code: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
        this._messageService.add({
          severity: 'success',
          summary: 'Stanowisko sparowane',
          detail: result.isExistingDevice
            ? `Stanowisko „${result.deviceName}” zostało połączone ponownie. Za kilka sekund pokaże ekran OSK.`
            : `Stanowisko „${result.deviceName}” zostało dodane. Za kilka sekund pokaże ekran OSK.`,
          life: 8000,
        });
        // The station collects its registration on its next poll, a few seconds from now.
        clearTimeout(this._devicesRefreshTimer);
        this._devicesRefreshTimer = setTimeout(() => this.loadDevices(), 5000);
      },
      error: (error: HttpErrorResponse) => {
        this.pairing = false;
        this.pairingError = this.describePairingError(error);
      },
    });
  }

  private formatCode(value: string): string {
    const characters = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 8);
    return characters.length > 4 ? `${characters.slice(0, 4)}-${characters.slice(4)}` : characters;
  }

  /** The confirmation message is HTML and the station name is typed by the OSK. */
  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private describePairingError(error: HttpErrorResponse): string {
    switch (error.status) {
      case 404:
        return 'Nie ma takiego kodu albo już wygasł. Sprawdź kod na ekranie stanowiska – nowy pojawia się co 10 minut.';
      case 409:
        return 'Ten kod został już potwierdzony. Stanowisko powinno za chwilę pokazać ekran OSK.';
      case 429:
        return 'Zbyt wiele prób. Spróbuj ponownie za kilka minut.';
      case 403:
        return 'To konto nie może dodawać stanowisk tego OSK.';
      default:
        return 'Nie udało się sparować stanowiska. Spróbuj ponownie.';
    }
  }
}
